import { useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { useFightStore } from './store'
import { fighters } from './content'
import { getAttackElementalFx, resolveElementalFx, type ElementalFxProfile } from './elyonElementalFx'
import { stageObjectDef } from './arenaStage'
import type { Combatant } from './simulation'
import type { ImpactCue } from './store'
import type { StageObjectState } from './stageDestruction'
import type { ArenaHazardState } from './arenaHazards'

const additive={blending:THREE.AdditiveBlending,depthWrite:false} as const

function profileColor(profile:ElementalFxProfile,index:number){return profile.primary[index%profile.primary.length]}

const flameVertex=`varying vec2 vUv; void main(){vUv=uv; vec3 p=position; p.x*=.62+.38*(1.0-uv.y); gl_Position=projectionMatrix*modelViewMatrix*vec4(p,1.0);}`
const flameFragment=`uniform vec3 uColor; varying vec2 vUv; void main(){float taper=.46*(1.0-vUv.y)+.055; float d=abs(vUv.x-.5); float body=1.0-smoothstep(taper*.48,taper,d); float base=smoothstep(.015,.15,vUv.y); float fade=1.0-smoothstep(.48,1.0,vUv.y); float ripple=.9+.1*sin(vUv.y*18.0); float a=body*base*fade*ripple*.58; if(a<.015)discard; gl_FragColor=vec4(uColor,a);}`
function FlameSheet({color,scale=1,rotation=0,offset=0}:{color:string;scale?:number;rotation?:number;offset?:number}){
 return <mesh position={[offset,.34,0]} rotation={[0,rotation,0]} scale={[scale,scale,scale]}>
  <planeGeometry args={[.44,1.2,1,1]}/><shaderMaterial vertexShader={flameVertex} fragmentShader={flameFragment} uniforms={{uColor:{value:new THREE.Color(color)}}} transparent depthWrite={false} blending={THREE.NormalBlending} side={THREE.DoubleSide}/>
 </mesh>
}
function EffectCluster({profile,scale=1}:{profile:ElementalFxProfile;scale?:number}){
  if(profile.particle==='flame'){
    return <group>
      <FlameSheet color={profile.primary[0]} scale={scale*.78} rotation={0} offset={-.12}/>
      <FlameSheet color={profile.primary[1]} scale={scale*.92} rotation={Math.PI/2} offset={.1}/>
      <FlameSheet color={profile.primary[2]} scale={scale*.62} rotation={Math.PI/4} offset={0}/>
      <mesh rotation={[Math.PI/2,0,0]} scale={scale*.82}><torusGeometry args={[.42,.018,6,28]}/><meshBasicMaterial color={profile.primary[1]} transparent opacity={.32} blending={THREE.NormalBlending} depthWrite={false}/></mesh>
    </group>
  }
  if(profile.particle==='droplet'){
    return <group>
      {[0,1,2,3].map((index)=>{const angle=index/4*Math.PI*2;return <mesh key={index} position={[Math.cos(angle)*.46,(index%2)*.17,Math.sin(angle)*.46]} scale={scale}>
        <sphereGeometry args={[.13,10,10]}/>
        <meshBasicMaterial color={profileColor(profile,index)} transparent opacity={.78} {...additive}/>
      </mesh>})}
      <mesh rotation={[-Math.PI/2,0,0]} scale={scale}>
        <torusGeometry args={[.55,.035,8,28]}/>
        <meshBasicMaterial color={profile.accent} transparent opacity={.66} {...additive}/>
      </mesh>
    </group>
  }
  if(profile.particle==='ribbon'){
    return <group rotation={[0,0,.2]}>
      {[0,1].map((index)=><mesh key={index} rotation={[0,index*.8,Math.PI/2]} scale={scale*(1+index*.24)}>
        <torusGeometry args={[.5,.035,6,28,Math.PI*1.55]}/>
        <meshBasicMaterial color={profileColor(profile,index)} transparent opacity={.66-index*.14} {...additive}/>
      </mesh>)}
      <mesh position={[0,.08,0]} scale={scale}>
        <cylinderGeometry args={[.035,.13,1.25,8]}/>
        <meshBasicMaterial color={profile.accent} transparent opacity={.58} {...additive}/>
      </mesh>
    </group>
  }
  if(profile.particle==='cloud'||profile.particle==='corrosion'){
    return <group scale={scale}>
      {[0,1,2].map((index)=><mesh key={index} position={[(index-1)*.32,.12+(index%2)*.18,(index%2-.5)*.25]}>
        <sphereGeometry args={[.3+index*.04,12,10]}/>
        <meshBasicMaterial color={profileColor(profile,index)} transparent opacity={profile.particle==='corrosion'?.3:.25} {...additive}/>
      </mesh>)}
      <mesh rotation={[-Math.PI/2,0,0]} position={[0,-.24,0]}>
        <circleGeometry args={[.72,28]}/>
        <meshBasicMaterial color={profile.accent} transparent opacity={.2} {...additive}/>
      </mesh>
    </group>
  }
  if(profile.particle==='shard'){
    return <group scale={scale}>
      {[0,1,2,3].map((index)=>{const angle=index/4*Math.PI*2;return <mesh key={index} position={[Math.cos(angle)*.48,(index%2)*.16,Math.sin(angle)*.48]} rotation={[angle,.4,index*.5]}>
        <tetrahedronGeometry args={[.18,0]}/>
        <meshBasicMaterial color={profileColor(profile,index)} transparent opacity={.78} {...additive}/>
      </mesh>})}
    </group>
  }
  return <group scale={scale}>
    {[0,1,2,3].map((index)=>{const angle=index/4*Math.PI*2;return <mesh key={index} position={[Math.cos(angle)*.45,Math.sin(angle)*.32,(index%2-.5)*.2]} rotation={[angle,.4,index]}>
      <octahedronGeometry args={[.11,0]}/>
      <meshBasicMaterial color={profileColor(profile,index)} transparent opacity={.82} {...additive}/>
    </mesh>})}
  </group>
}

function AttackElementalVfx({fighterId,actor}:{fighterId:string;actor:'player'|'enemy'}){
  const root=useRef<THREE.Group>(null)
  const attackPhase=useFightStore(state=>(actor==='player'?state.player:state.enemy).attackPhase)
  const lastAttack=useFightStore(state=>(actor==='player'?state.player:state.enemy).lastAttack)
  const profile=getAttackElementalFx(fighterId,lastAttack)
  useFrame(({clock},dt)=>{
    if(!root.current)return
    const state=useFightStore.getState(),self=actor==='player'?state.player:state.enemy,other=actor==='player'?state.enemy:state.player
    const direction=self.x<=other.x?1:-1
    root.current.position.set(self.x+direction*(lastAttack==='special'?1.05:.82),1.18,self.z)
    root.current.rotation.y+=dt*(lastAttack==='special'?2.8:lastAttack==='heavy'?1.8:.9)
    const pulse=1+Math.sin(clock.elapsedTime*18)*(lastAttack==='special'?.08:.04)
    root.current.scale.setScalar(pulse)
  })
  if(!attackPhase||!lastAttack)return null
  const intensity=lastAttack==='special'?1.35:lastAttack==='heavy'?1.05:.78
  return <group ref={root}>
    <EffectCluster profile={profile} scale={intensity}/>
    <pointLight color={profile.accent} intensity={lastAttack==='special'?5.2:lastAttack==='heavy'?3.1:1.7} distance={4.8} decay={2}/>
  </group>
}
function ImpactElementalVfx({cue}:{cue:ImpactCue}){
  const root=useRef<THREE.Group>(null)
  const profile=resolveElementalFx(cue.elementalFxId)
  const intensity=cue.kind==='guard-break'?1.45:cue.kind==='parry'?1.2:cue.kind==='throw'?1.05:.9
  useFrame(({clock},dt)=>{
    if(!root.current)return
    root.current.rotation.y+=dt*(profile.element==='fire'?2.5:1.35)
    const pulse=1+Math.sin(clock.elapsedTime*20)*.06
    root.current.scale.setScalar(pulse)
  })
  const target=cue.stageObjectId?stageObjectDef(cue.stageObjectId):undefined,position:[number,number,number]=target?[target.position[0],Math.max(.65,target.position[1]*.55),target.position[2]]:[cue.x,1.12,cue.z]
  return <group ref={root} position={position}>
    <EffectCluster profile={profile} scale={intensity}/>
    <mesh rotation={[-Math.PI/2,0,0]} position={[0,-1.07,0]}>
      <ringGeometry args={[profile.arena.radius*.55,profile.arena.radius*.63,40]}/>
      <meshBasicMaterial color={profile.accent} transparent opacity={.24*profile.arena.intensity} {...additive}/>
    </mesh>
    {profile.arena.hazard!=='none'&&<mesh rotation={[-Math.PI/2,0,0]} position={[0,-1.06,0]}>
      <circleGeometry args={[profile.arena.radius,40]}/>
      <meshBasicMaterial color={profile.primary[1]} transparent opacity={.08*profile.arena.intensity} {...additive}/>
    </mesh>}
    <pointLight color={profile.accent} intensity={4.6*profile.arena.intensity} distance={6} decay={2}/>
  </group>
}

const STAGE_COLORS:Record<string,string>={
  stone:'#d4bca0',
  crystal:'#b783ff',
  wood:'#b9784f',
  metal:'#8bb8d8',
  organic:'#6be27c',
  earth:'#a78b62',
  water:'#5bc8ff',
}

function PersistentHazardVfx({hazard}:{hazard:ArenaHazardState}){
 const root=useRef<THREE.Group>(null),profile=resolveElementalFx(hazard.elementalFxId),life=Math.max(0,Math.min(1,hazard.remaining/Math.max(.001,hazard.lifetime)))
 useFrame(({clock},dt)=>{if(!root.current)return;root.current.rotation.y+=dt*(profile.element==='fire'?1.5:.45);root.current.position.y=.025+Math.sin(clock.elapsedTime*2.2+hazard.id)*.012})
 const radius=hazard.radius,clusterScale=.32+hazard.intensity*.34
 return <group ref={root} position={[hazard.x,.03,hazard.z]}>
  <mesh rotation={[-Math.PI/2,0,0]}><circleGeometry args={[radius,48]}/><meshBasicMaterial color={profile.primary[1]} transparent opacity={.035+.07*life*hazard.intensity} {...additive}/></mesh>
  <mesh rotation={[-Math.PI/2,0,0]} position={[0,.006,0]}><ringGeometry args={[radius*.74,radius*.8,48]}/><meshBasicMaterial color={profile.accent} transparent opacity={.09+.16*life} {...additive}/></mesh>
  <group position={[0,.18,0]} scale={.55+.45*life}><EffectCluster profile={profile} scale={clusterScale}/></group>
  <pointLight color={profile.accent} intensity={1.2*hazard.intensity*life} distance={Math.max(3,radius*1.8)} decay={2}/></group>
}


function ActiveStageHazardVfx({object,def}:{object:StageObjectState;def:NonNullable<ReturnType<typeof stageObjectDef>>}){
  const hazard=object.hazard
  if(!hazard)return null
  const profile=resolveElementalFx(hazard.elementalFxId)
  const life=Math.max(0,Math.min(1,hazard.remaining/Math.max(.001,hazard.duration)))
  const radius=Math.max(.45,profile.arena.radius)
  return <group position={[def.position[0],.06,def.position[2]]}>
    <mesh rotation={[-Math.PI/2,0,0]}>
      <ringGeometry args={[radius*.72,radius,40]}/>
      <meshBasicMaterial color={profile.accent} transparent opacity={(.16+.24*life)*profile.arena.intensity} {...additive}/>
    </mesh>
    <mesh rotation={[-Math.PI/2,0,0]} position={[0,.01,0]}>
      <circleGeometry args={[radius*.68,40]}/>
      <meshBasicMaterial color={profile.primary[1]} transparent opacity={(.035+.075*life)*profile.arena.intensity} {...additive}/>
    </mesh>
    <group position={[0,.38,0]}>
      <EffectCluster profile={profile} scale={.28+.24*life}/>
    </group>
    <pointLight color={profile.accent} intensity={(1.4+4.6*life)*profile.arena.intensity} distance={Math.max(3.2,radius*2.2)} decay={2}/>
  </group>
}

function StageDestructionVfx({objects}:{objects:StageObjectState[]}){
  const root=useRef<THREE.Group>(null)
  useFrame(({clock})=>{if(root.current)root.current.rotation.y=Math.sin(clock.elapsedTime*.22)*.008})
  const active=objects
    .map(object=>{
      const def=stageObjectDef(object.id)
      if(!def)return null
      const damage=1-object.integrity/Math.max(1,object.maxIntegrity)
      if(damage<=.01&&!object.hazard)return null
      return{object,def,damage}
    })
    .filter((entry):entry is NonNullable<typeof entry>=>Boolean(entry))
    .sort((a,b)=>(b.object.state==='collapsed'?2:0)+(b.damage)-(a.object.state==='collapsed'?2:0)-a.damage)
    .slice(0,3)
  return <group ref={root}>{active.map(({object,def,damage},slot)=>{
    const profile=resolveElementalFx(object.lastElementalFxId??object.hazard?.elementalFxId??'physical')
    const hazardProfile=object.hazard?resolveElementalFx(object.hazard.elementalFxId):null
    const color=hazardProfile?.primary[1]??(object.lastElementalFxId?profile.primary[1]:STAGE_COLORS[object.material??'stone']??'#d4bca0')
    const collapsed=object.state==='collapsed',radius=.4+damage*.62,primary=slot===0
    return <group key={object.id} position={[def.position[0],collapsed?.16:def.position[1],def.position[2]]}>
      <mesh rotation={[-Math.PI/2,0,0]}>
        <ringGeometry args={[radius,radius+.028+damage*.024,24]}/>
        <meshBasicMaterial color={color} transparent opacity={.12+damage*.26} {...additive}/>
      </mesh>
      {primary&&damage>.24&&<group position={[0,.18,0]}><EffectCluster profile={profile} scale={(collapsed?.82:.42)+damage*.28}/></group>}
      {collapsed&&<mesh rotation={[-Math.PI/2,0,0]} scale={1.35}><ringGeometry args={[radius*.8,radius*.87,24]}/><meshBasicMaterial color={profile.accent} transparent opacity={.22} {...additive}/></mesh>}
      {primary&&<pointLight color={profile.accent} intensity={(collapsed?2.1:.72)*damage} distance={collapsed?3.6:2.4} decay={2}/>}
      {primary&&object.hazard&&<ActiveStageHazardVfx object={object} def={def}/>}
    </group>
  })}</group>
}
export function ElyonElementalArenaVfx({arenaId}:{arenaId:string}){
  const playerIndex=useFightStore((state)=>state.playerIndex)
  const enemyIndex=useFightStore((state)=>state.enemyIndex)
  const impactCue=useFightStore((state)=>state.impactCue)
  const stageObjects=useFightStore((state)=>state.stageObjects)
  const arenaHazards=useFightStore((state)=>state.arenaHazards)
  const qaParams=typeof window!=='undefined'&&(window.location.hostname==='127.0.0.1'||window.location.hostname==='localhost')?new URLSearchParams(window.location.search):null
  const qaOff=(name:string)=>qaParams?.get(name)==='1'
  return <group>
    {!qaOff('qaNoAttackElemental')&&<><AttackElementalVfx fighterId={fighters[playerIndex]?.id??'elyon-protagonist'} actor="player"/><AttackElementalVfx fighterId={fighters[enemyIndex]?.id??'elyon-protagonist'} actor="enemy"/></>}
    {!qaOff('qaNoImpactElemental')&&impactCue&&<ImpactElementalVfx key={impactCue.serial} cue={impactCue}/>}
    {!qaOff('qaNoHazardElemental')&&arenaHazards.map(hazard=><PersistentHazardVfx key={hazard.id+'-'+hazard.elementalFxId} hazard={hazard}/>)}
    {!qaOff('qaNoDestructionElemental')&&<StageDestructionVfx key={arenaId} objects={stageObjects}/>}
  </group>
}